/** AT01 copy (one group per control; a ticket edits only its own file). */
export const at01PtBr = {
  title: 'Classificação de aquisições e projetos intangíveis',
  description:
    'Compras ainda sem decisão de capitalização e projetos intangíveis em pesquisa ou desenvolvimento.',
  prerequisite:
    'Requer a leitura da fila de capitalização e do ciclo de vida dos projetos intangíveis.',
  reasons: {
    CANDIDATE_CAPITALIZE_UNDECIDED: 'Proposta de capitalização aguardando decisão',
    CANDIDATE_EXPENSE_UNDECIDED: 'Proposta de despesa aguardando decisão',
    CANDIDATE_PROJECT_UNDECIDED: 'Proposta de projeto intangível aguardando decisão',
    CANDIDATE_LEASE_SERVICE_UNDECIDED: 'Proposta de pagamento de arrendamento aguardando decisão',
    CANDIDATE_LEASE_ASSESSMENT_UNDECIDED: 'Avaliação de arrendamento aguardando decisão',
    CANDIDATE_RETIRED_NOT_REDECIDED:
      'Decisão desfeita pela reclassificação da conta e ainda não tomada de novo',
    CAPITALIZATION_WITHOUT_ASSET: 'Capitalização confirmada sem bem criado',
    ASSET_DRAFT_NOT_IN_SERVICE: 'Bem em rascunho, ainda não colocado em uso',
    LINE_ABOVE_THRESHOLD_WITHOUT_DECISION:
      'Linha acima do limite de capitalização sem proposta nem decisão',
    RESEARCH_COST_CAPITALISED: 'Custo de pesquisa capitalizado',
    DEVELOPMENT_GATE_MISSING: 'Projeto em desenvolvimento sem certificação das condições do CPC 04',
    DEVELOPMENT_GATE_INCOMPLETE: 'Certificação do CPC 04 sem todas as condições marcadas',
    CAPITALISED_BEFORE_GATE: 'Custo capitalizado antes da competência de início do desenvolvimento',
    IN_SERVICE_WITHOUT_ASSET: 'Projeto em uso sem bem',
    IN_SERVICE_COST_DIFFERS: 'Custo do bem difere do custo capitalizado no projeto',
    WRITE_OFF_INCOMPLETE: 'Baixa sem motivo ou sem data de efeito',
    WRITTEN_OFF_WITH_ASSET: 'Projeto baixado ainda ligado a um bem',
    ART42_WITHOUT_FISCAL_MEMO:
      'Exclusão do art. 42 sem registro no livro fiscal do mesmo tipo, competência e valor',
    ART42_MEMO_WITHOUT_ELIGIBLE_COST:
      'Exclusão do art. 42 no livro fiscal sem custo capitalizado elegível por trás',
  },
  observations: {
    AT01_SCOPE_UNRESOLVED: 'Moeda funcional do livro não resolvida',
    AT01_CAPITALIZATION_POLICY_MISSING:
      'Contas a pagar do período não avaliadas: a entidade não tem limite de capitalização',
    AT01_LINE_UNMEASURABLE: 'Linhas em moeda estrangeira sem taxa de provisão, não medidas',
    AT01_GATE_TEMPLATE_MISSING: 'Projetos cujo modelo de condições do CPC 04 não foi encontrado',
    AT01_SUPPLIER_ADVANCES_NOT_JUDGED:
      'Adiantamentos a fornecedor em aberto no fim do período, um por fornecedor e moeda, fora da avaliação de capitalização',
    AT01_GATE_NOT_MEASURABLE_AT_CUTOFF:
      'Projetos cuja certificação vigente no fim do período foi substituída depois, não avaliados',
    AT01_LINES_NOT_ASSET_LIKE:
      'Linhas acima do limite que não podem ser um bem (sem NCM, sinal de imobilizado ou conta de ativo)',
    AT01_RETIRED_DECISION_PROJECT_CLOSED:
      'Decisões desfeitas cujo projeto já entrou em uso ou foi baixado',
    AT01_ART42_NOT_APPLICABLE:
      'Projetos com custo excluído pelo art. 42 em entidade sem livro fiscal ativo: art. 42 não avaliado',
    AT01_PROJECTS_OVER_LIMIT:
      'Projetos intangíveis não avaliados: há mais projetos ou linhas do que uma avaliação comporta',
    AT01_QUEUE_OVER_LIMIT:
      'Fila de capitalização não avaliada: há mais candidatos ou contas do período do que uma avaliação comporta',
    AT01_PROJECT_OTHER_FRAMEWORK:
      'Projetos intangíveis de outro referencial contábil, avaliados nos livros desse referencial',
  },
  causes: {
    FUNCTIONAL_CURRENCY_UNRESOLVED: 'Moeda funcional do livro não resolvida',
    CAPITALIZATION_POLICY_MISSING: 'Entidade sem limite de capitalização',
    PROVISION_RATE_MISSING: 'Taxa de provisão ausente',
    PROVISION_RATE_INVALID: 'Taxa de provisão inválida',
    SUPPLIER_ADVANCE_OPEN: 'Adiantamento em aberto no fim do período',
    GATE_SUPERSEDED_AFTER_CUTOFF: 'Certificação vigente no fim do período foi substituída depois',
    GATE_TEMPLATE_MISSING: 'Modelo de condições do CPC 04 não encontrado',
    PROJECTS_OVER_LIMIT:
      'Mais projetos ou linhas do que o limite de uma avaliação (projetos · custos · registros fiscais, em uso/limite)',
    QUEUE_OVER_LIMIT:
      'Mais candidatos ou contas do que o limite de uma avaliação (candidatos · contas do período, em uso/limite)',
  },
  vendor: 'Fornecedor {{code}}',
  scope: 'Livro {{code}}',
  refusalReference: 'Referência: {{value}}',
  refusalCutoff: 'Avaliado em {{date}}',
  refusalTruncated: 'Mostrando {{shown}} de {{total}} registros.',
  bill: 'Conta a pagar {{code}}',
  asset: 'Bem {{code}}',
  project: 'Projeto {{code}}',
  openBill: 'Abrir conta a pagar',
  openAsset: 'Abrir bem',
  openProject: 'Abrir projeto',
  openQueue: 'Abrir fila de revisão',
  listTruncated: 'Mostrando {{shown}} de {{total}} apontamentos.',
};

export const at01En: typeof at01PtBr = {
  title: 'Acquisition classification and intangible projects',
  description:
    'Purchases still waiting for a capitalization decision and intangible projects in research or development.',
  prerequisite: 'Needs the capitalization queue and the intangible project lifecycle.',
  reasons: {
    CANDIDATE_CAPITALIZE_UNDECIDED: 'Capitalization proposal waiting for a decision',
    CANDIDATE_EXPENSE_UNDECIDED: 'Expense proposal waiting for a decision',
    CANDIDATE_PROJECT_UNDECIDED: 'Intangible project proposal waiting for a decision',
    CANDIDATE_LEASE_SERVICE_UNDECIDED: 'Lease payment proposal waiting for a decision',
    CANDIDATE_LEASE_ASSESSMENT_UNDECIDED: 'Lease assessment waiting for a decision',
    CANDIDATE_RETIRED_NOT_REDECIDED:
      'Decision undone by the bill reclassification and not taken again',
    CAPITALIZATION_WITHOUT_ASSET: 'Confirmed capitalization with no asset created',
    ASSET_DRAFT_NOT_IN_SERVICE: 'Asset in draft, not yet placed in service',
    LINE_ABOVE_THRESHOLD_WITHOUT_DECISION:
      'Line above the capitalization threshold with no proposal or decision',
    RESEARCH_COST_CAPITALISED: 'Research cost capitalized',
    DEVELOPMENT_GATE_MISSING: 'Project in development with no CPC 04 conditions certified',
    DEVELOPMENT_GATE_INCOMPLETE: 'CPC 04 certification missing some conditions',
    CAPITALISED_BEFORE_GATE: 'Cost capitalized before the development start accounting period',
    IN_SERVICE_WITHOUT_ASSET: 'Project in service with no asset',
    IN_SERVICE_COST_DIFFERS: 'Asset cost differs from the cost capitalized on the project',
    WRITE_OFF_INCOMPLETE: 'Write-off with no reason or no effective date',
    WRITTEN_OFF_WITH_ASSET: 'Written-off project still linked to an asset',
    ART42_WITHOUT_FISCAL_MEMO:
      'Art. 42 exclusion with no fiscal memo of the same kind, accounting period and amount',
    ART42_MEMO_WITHOUT_ELIGIBLE_COST:
      'Art. 42 exclusion in the fiscal memo with no eligible capitalized cost behind it',
  },
  observations: {
    AT01_SCOPE_UNRESOLVED: 'Book functional currency not resolved',
    AT01_CAPITALIZATION_POLICY_MISSING:
      'Bills of the period not assessed: the entity has no capitalization threshold',
    AT01_LINE_UNMEASURABLE: 'Foreign-currency lines with no provision rate, not measured',
    AT01_GATE_TEMPLATE_MISSING: 'Projects whose CPC 04 conditions template was not found',
    AT01_SUPPLIER_ADVANCES_NOT_JUDGED:
      'Supplier advances still open at period end, one per supplier and currency, outside the capitalization assessment',
    AT01_GATE_NOT_MEASURABLE_AT_CUTOFF:
      'Projects whose certification in force at period end was replaced later, not assessed',
    AT01_LINES_NOT_ASSET_LIKE:
      'Lines above the threshold that cannot be an asset (no NCM, fixed-asset signal or asset account)',
    AT01_RETIRED_DECISION_PROJECT_CLOSED:
      'Undone decisions whose project is already in service or written off',
    AT01_ART42_NOT_APPLICABLE:
      'Projects with art. 42 excluded cost in an entity with no active fiscal book: art. 42 not assessed',
    AT01_PROJECTS_OVER_LIMIT:
      'Intangible projects not assessed: more projects or lines than one assessment can hold',
    AT01_QUEUE_OVER_LIMIT:
      'Capitalization queue not assessed: more candidates or period bills than one assessment can hold',
    AT01_PROJECT_OTHER_FRAMEWORK:
      'Intangible projects under another accounting framework, assessed on that framework’s books',
  },
  causes: {
    FUNCTIONAL_CURRENCY_UNRESOLVED: 'Book functional currency not resolved',
    CAPITALIZATION_POLICY_MISSING: 'Entity has no capitalization threshold',
    PROVISION_RATE_MISSING: 'Provision rate missing',
    PROVISION_RATE_INVALID: 'Provision rate invalid',
    SUPPLIER_ADVANCE_OPEN: 'Advance still open at period end',
    GATE_SUPERSEDED_AFTER_CUTOFF: 'Certification in force at period end was replaced later',
    GATE_TEMPLATE_MISSING: 'CPC 04 conditions template not found',
    PROJECTS_OVER_LIMIT:
      'More projects or lines than one assessment allows (projects · costs · fiscal memos, found/limit)',
    QUEUE_OVER_LIMIT:
      'More candidates or bills than one assessment allows (candidates · period bills, found/limit)',
  },
  vendor: 'Supplier {{code}}',
  scope: 'Book {{code}}',
  refusalReference: 'Reference: {{value}}',
  refusalCutoff: 'Assessed as of {{date}}',
  refusalTruncated: 'Showing {{shown}} of {{total}} records.',
  bill: 'Bill {{code}}',
  asset: 'Asset {{code}}',
  project: 'Project {{code}}',
  openBill: 'Open bill',
  openAsset: 'Open asset',
  openProject: 'Open project',
  openQueue: 'Open review queue',
  listTruncated: 'Showing {{shown}} of {{total}} findings.',
};
